import { Dropdown, Button, Tooltip, Space } from "antd";
import { MoreOutlined } from "@ant-design/icons";
import hasPermission from "../PermissionHocs/hasPermission";
import { ACTION_COLOR, ACTION_ICON } from "./actions";

/**
 * Cụm hành động của một dòng bảng — bố cục chuẩn cho mọi màn danh sách.
 *
 * Hình dạng: [nút Xem] [nút "…"]. Xem tách riêng vì là thao tác dùng nhiều nhất,
 * bắt người dùng mở dropdown mỗi lần chỉ để xem là thừa một cú bấm. Các hành động
 * còn lại (sửa/xóa/đổi trạng thái…) nằm trong dropdown để cột "Thao tác" không
 * phình ngang theo số lượng hành động.
 *
 * Gom cả phân quyền vào đây: audit thấy chỉ ~12/39 màn bọc `ButtonWithPermission`,
 * còn lại nút trần nên người không có quyền vẫn thấy nút bình thường rồi bấm vào
 * mới nhận lỗi từ BE. Truyền `permission` là tự khóa nút + hiện lý do.
 *
 * @param {Function} [onView]           có thì hiện nút Xem ngoài cùng
 * @param {string[]} [viewPermission]   quyền cần có để xem
 * @param {Array} items  [{ key?, label, icon?, onClick, danger?, disabled?,
 *                          divider?, permission?: string[] }]
 * @param {boolean} [disabled]  khóa toàn cụm
 *
 * @example
 * <RowActions
 *   onView={() => navigate(`/customer-detail/${r.id}`)}
 *   items={[
 *     { key: "edit", label: "Chỉnh sửa", icon: ACTION_ICON.edit,
 *       permission: ["mcoupon.customer.update"], onClick: () => onEdit(r) },
 *     { key: "delete", label: "Xóa", icon: ACTION_ICON.delete, danger: true,
 *       permission: ["mcoupon.customer.delete"],
 *       onClick: () => confirmDelete(r.name, () => remove(r.id)) },
 *   ]}
 * />
 */
export default function RowActions({
  onView,
  viewPermission,
  viewTooltip = "Xem chi tiết",
  items = [],
  disabled = false,
}) {
  const canView = !viewPermission || hasPermission({ actions: viewPermission });

  const menuItems = [];
  items.filter(Boolean).forEach((it, idx) => {
    const allowed = !it.permission || hasPermission({ actions: it.permission });

    if (it.divider) menuItems.push({ type: "divider", key: `divider-${idx}` });

    menuItems.push({
      key: it.key ?? String(idx),
      label: allowed ? (
        it.label
      ) : (
        // Vẫn hiện mục nhưng khóa lại + nói rõ lý do, thay vì ẩn đi khiến người
        // dùng tưởng chức năng không tồn tại rồi đi hỏi.
        <Tooltip title="Bạn không có quyền thực hiện thao tác này">
          <span>{it.label}</span>
        </Tooltip>
      ),
      icon: it.icon,
      danger: it.danger,
      disabled: it.disabled || !allowed,
      onClick: (info) => {
        info?.domEvent?.stopPropagation?.();
        it.onClick?.();
      },
    });
  });

  return (
    <Space size={4} onClick={(e) => e.stopPropagation()}>
      {onView && (
        <Tooltip title={canView ? viewTooltip : "Bạn không có quyền xem"}>
          {/* span bọc ngoài: Tooltip của antd không bắt được sự kiện chuột trên
              button đang disabled, không bọc thì hover không ra tooltip. */}
          <span>
            <Button
              type="text"
              size="small"
              disabled={disabled || !canView}
              icon={ACTION_ICON.view}
              style={{ color: canView ? ACTION_COLOR.view : undefined }}
              onClick={onView}
            />
          </span>
        </Tooltip>
      )}

      {menuItems.length > 0 && (
        <Dropdown
          menu={{ items: menuItems }}
          trigger={["click"]}
          placement="bottomRight"
          disabled={disabled}
        >
          <Tooltip title="Thao tác khác">
            <Button type="text" size="small" icon={<MoreOutlined />} />
          </Tooltip>
        </Dropdown>
      )}
    </Space>
  );
}
